import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SHIFT_META, ROLE_LABELS, WEEKDAY_CODES, WEEKEND_CODES } from '@/lib/constants';
import { buildDays, weekdayShort, dayNum, isWeekend, isoAddDays } from '@/lib/dates';

export default function ScheduleGrid({ schedule, employees, workplace, onCellChange }) {
  if (!schedule) return null;
  const days = buildDays(schedule.start_date, schedule.num_days);
  const assignments = schedule.assignments || {};
  const shiftHoursByCode = Object.fromEntries(((workplace && workplace.shifts) || []).map((s) => [s.code, s.hours]));
  const restWeeks = {};
  (schedule.stats?.per_employee || []).forEach((e) => {
    restWeeks[e.id] = new Set((e.weeks || []).filter((w) => w.full_week && !w.rest_ok).map((w) => w.week_start));
  });
  const hoursById = {};
  (schedule.stats?.per_employee || []).forEach((e) => { hoursById[e.id] = e.total_hours; });
  const compRest = schedule.stats?.comp_rest || {};
  const compRestReason = schedule.stats?.comp_rest_reason || {};

  const weekStartOf = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return isoAddDays(iso, -((dt.getUTCDay() + 6) % 7));
  };

  return (
    <div data-testid="schedule-grid" className="overflow-x-auto border border-slate-200 rounded-md bg-white w-full">
      <table className="border-collapse text-center w-full" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th className="bg-slate-100 border border-slate-200 p-1.5 text-[11px] font-semibold uppercase text-slate-500 sticky left-0 top-0 z-30 text-left w-[124px]">
              Zamestnanec
            </th>
            {days.map((iso) => (
              <th
                key={iso}
                className={`border border-slate-200 px-0 py-0.5 text-[10px] font-medium sticky top-0 z-10 ${isWeekend(iso) ? 'bg-slate-200 text-slate-700' : 'bg-slate-50 text-slate-500'}`}
              >
                <div className="font-semibold text-slate-700 text-[11px] leading-tight">{dayNum(iso)}</div>
                <div className="text-[8px] uppercase leading-tight">{weekdayShort(iso)}</div>
              </th>
            ))}
            <th className="bg-slate-100 border border-slate-200 px-0.5 text-[10px] font-semibold uppercase text-slate-500 sticky top-0 z-10 w-[34px]">Σh</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp) => {
            const isFlex = !emp.is_virtual && (emp.weekly_hours === 0 || emp.weekly_hours === '0');
            const isBrig = emp.role === 'brigadnik';
            const rowBg = emp.is_virtual ? 'bg-amber-50' : (isBrig ? 'bg-orange-50' : (isFlex ? 'bg-teal-50/40' : ''));
            const stickyBg = emp.is_virtual ? 'bg-amber-50' : (isBrig ? 'bg-orange-50' : (isFlex ? 'bg-teal-50/40' : 'bg-white'));
            return (
            <tr key={emp.id} className={rowBg}>
              <td className={`border border-slate-200 sticky left-0 z-20 text-left px-2 py-1 shadow-[1px_0_0_0_#e2e8f0] w-[124px] ${stickyBg}`}>
                <div className="font-medium text-slate-900 text-[12px] leading-tight flex items-center gap-1 truncate">
                  {emp.is_virtual && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />}
                  {isBrig && <span data-testid={`grid-brig-dot-${emp.id}`} title="Brigádnik / dohoda – výpomoc, nasadzuje sa len na výplň" className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />}
                  {isFlex && <span data-testid={`flex-dot-${emp.id}`} title="Flexibilný / na výpomoc – bez pevného fondu, nasadí sa len pri potrebe pokrytia" className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />}
                  <span className="truncate">{emp.name}</span>
                </div>
                <div className={`text-[10px] leading-tight ${emp.is_virtual ? 'text-amber-600 font-medium' : (isBrig ? 'text-orange-600 font-medium' : (isFlex ? 'text-teal-600 font-medium' : 'text-slate-500'))}`}>
                  {emp.is_virtual ? 'Personál navyše' : (isBrig ? 'Brigádnik · výpomoc' : (isFlex ? `${ROLE_LABELS[emp.role] || emp.role} · na výpomoc` : (ROLE_LABELS[emp.role] || emp.role)))}
                </div>
              </td>
              {days.map((iso) => {
                const code = (assignments[emp.id] || {})[iso] || 'X';
                const isNV = code === 'X' && (compRest[emp.id] || []).includes(iso);
                const meta = isNV ? { bg: '#DCFCE7', text: '#15803D', short: 'NV' } : (SHIFT_META[code] || SHIFT_META.X);
                const codes = isWeekend(iso) ? WEEKEND_CODES : WEEKDAY_CODES;
                const violate = code !== 'X' && code !== 'D' && restWeeks[emp.id]?.has(weekStartOf(iso));
                const nvReason = isNV ? (compRestReason[emp.id]?.[iso] || '') : '';
                const nvShort = nvReason.includes('minul') ? 'z min.m.'
                  : nvReason.replace('za sobotu ', 'So ').replace('za nedeľu ', 'Ne ');
                return (
                  <td key={iso} className="border border-slate-200 p-0">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid={`cell-${emp.id}-${iso}`}
                          className="w-full h-9 flex flex-col items-center justify-center font-semibold relative hover:ring-2 hover:ring-inset hover:ring-slate-400 transition-all"
                          style={{ backgroundColor: meta.bg, color: meta.text }}
                          title={isNV ? `Náhradné voľno (§93) za odpracovaný víkend: ${nvReason}` : undefined}
                        >
                          <span className="leading-none text-[12px]">{meta.short}</span>
                          {isNV
                            ? <span className="text-[7px] font-medium opacity-90 leading-none mt-0.5 px-0.5 truncate max-w-full">{nvShort}</span>
                            : (shiftHoursByCode[code] ? <span className="text-[7px] font-normal opacity-70 leading-none mt-0.5">{shiftHoursByCode[code]}h</span> : null)}
                          {violate && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" title="§93 odpočinok" />}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-2" align="center">
                        <div className="text-xs text-slate-500 mb-2 px-1">{emp.name} · {dayNum(iso)}. {weekdayShort(iso)}{isNV && nvReason ? ` · NV za víkend ${nvReason}` : ''}</div>
                        <div className="grid grid-cols-3 gap-1">
                          {codes.map((c) => {
                            const m = SHIFT_META[c];
                            return (
                              <button
                                key={c}
                                data-testid={`cell-opt-${emp.id}-${iso}-${c}`}
                                onClick={() => onCellChange(emp.id, iso, c)}
                                className="px-2 py-1.5 rounded text-xs font-medium border hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: m.bg, color: m.text, borderColor: m.border }}
                              >
                                {m.short || '—'}
                              </button>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </td>
                );
              })}
              <td className="border border-slate-200 text-center font-mono text-[10px] font-semibold text-slate-700 bg-slate-50 px-0.5">{hoursById[emp.id] ?? 0}h</td>
            </tr>
            );
          })}
        </tbody>
        <tfoot>
          {[[['R', 'C'], 'Ráno (R+C)'], [['P', 'C'], 'Poobede (P+C)']].map(([cs, label]) => (
            <tr key={label} data-testid={`footer-${cs[0]}`}>
              <td className="border border-slate-200 sticky left-0 z-20 bg-slate-100 text-left px-2 py-1 text-[10px] font-medium text-slate-600 shadow-[1px_0_0_0_#e2e8f0]">{label}</td>
              {days.map((iso) => {
                const n = employees.filter((e) => cs.includes((assignments[e.id] || {})[iso])).length;
                return <td key={iso} className="border border-slate-200 text-center text-[10px] font-mono text-slate-500 bg-slate-50">{isWeekend(iso) ? '' : (n || '')}</td>;
              })}
              <td className="border border-slate-200 bg-slate-50" />
            </tr>
          ))}
          <tr data-testid="footer-weekend">
            <td className="border border-slate-200 sticky left-0 z-20 bg-slate-100 text-left px-2 py-1 text-[10px] font-medium text-slate-600 shadow-[1px_0_0_0_#e2e8f0]">Víkend</td>
            {days.map((iso) => {
              const n = employees.filter((e) => ['SO', 'NE'].includes((assignments[e.id] || {})[iso])).length;
              return <td key={iso} className="border border-slate-200 text-center text-[10px] font-mono text-slate-500 bg-slate-50">{isWeekend(iso) ? (n || '') : ''}</td>;
            })}
            <td className="border border-slate-200 bg-slate-50" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
